import React from 'react';
import { 
  Box, 
  Typography, 
  IconButton,
  useTheme,
  alpha,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HistoryIcon from '@mui/icons-material/History';
import StarIcon from '@mui/icons-material/Star';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { motion } from 'framer-motion';
import AnimatedTabs from '../AnimatedTabs';
import GlowButton from '../GlowButton';

// Heineken logo component
const HeinekenLogo = () => {
  const theme = useTheme();
  
  return (
    <Box 
      component="div" 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1 
      }}
    >
      <Box 
        component="span" 
        sx={{ 
          color: '#FF0000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 0.5,
          filter: 'drop-shadow(0 0 2px rgba(255,0,0,0.5))',
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%': { opacity: 0.8 },
            '50%': { opacity: 1, transform: 'scale(1.1)' },
            '100%': { opacity: 0.8 }
          }
        }}
      >
        <StarIcon fontSize="small" sx={{ fontSize: '1.2rem' }} />
      </Box>
      <Typography 
        variant="h6" 
        component="span" 
        sx={{ 
          fontWeight: 700, 
          color: '#1B6B3F',
          letterSpacing: '0.5px'
        }}
      >
        HEINEKEN
      </Typography>
    </Box>
  );
};

/**
 * DashboardLayout - Shared layout component for both Current and Snapshot dashboards
 * Provides consistent header, navigation, and tab structure
 */
const DashboardLayout = ({
  title,
  subtitle,
  dataType = 'current', // 'current' or 'snapshot'
  month = null,
  availableSnapshots = [],
  onNavigate,
  onRefresh,
  tabs = [],
  activeTab = 0,
  onTabChange,
  children,
  loading = false,
  brandCount = 0,
  campaignCount = 0
}) => {
  const theme = useTheme();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: 'spring',
        stiffness: 300, 
        damping: 20
      }
    }
  };

  /**
   * Handle snapshot month change
   */
  // Snapshot month change handled via DatePicker onChange only

  /**
   * Handle date picker change
   */
  const handleDateChange = (newDate) => {
    if (newDate) {
      // Format date to YYYY-MM for snapshot month
      const formattedMonth = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`;
      if (onNavigate) {
        onNavigate(formattedMonth);
      }
    } else {
      // If date is cleared, navigate to live data
      if (onNavigate) {
        onNavigate('');
      }
    }
  };

  /**
   * Handle refresh action
   */
  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  return (
    <Box 
      component="main" 
      sx={{ 
        flexGrow: 1, 
        p: { xs: 2, md: 3 }, 
        bgcolor: 'background.default', 
        minHeight: '100vh',
        maxWidth: '100vw',
        overflow: 'hidden'
      }}
    >
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header Section */}
        <motion.div variants={itemVariants}>
          <Box 
            sx={{
              mb: 3,
              display: 'flex',
              flexDirection: {xs: 'column', md: 'row'},
              justifyContent: 'space-between',
              alignItems: {xs: 'flex-start', md: 'center'},
              gap: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <HeinekenLogo />
              <Box sx={{ ml: 4 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  {title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {subtitle}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Month Calendar Selector */}
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Select Month"
                  value={month ? new Date(`${month}-01`) : null}
                  onChange={handleDateChange}
                  views={['year', 'month']}
                  openTo="month"
                  referenceDate={month ? new Date(`${month}-01`) : undefined}
                  defaultCalendarMonth={month ? new Date(`${month}-01`) : new Date()}
                  disableHighlightToday
                  sx={{ 
                    minWidth: 200,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: alpha(theme.palette.primary.light, 0.05),
                      height: '40px',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.light, 0.1)
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontWeight: 500,
                      fontSize: '0.875rem'
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: '0.875rem',
                      py: 1
                    }
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      placeholder: 'Select month for snapshot data',
                      InputProps: {
                        startAdornment: (
                          <CalendarTodayIcon 
                            sx={{ 
                              mr: 1, 
                              fontSize: '1rem', 
                              color: theme.palette.primary.main 
                            }} 
                          />
                        ),
                      }
                    },
                    actionBar: {
                      actions: ['clear', 'today', 'cancel', 'accept'],
                    },
                  }}
                />
              </LocalizationProvider>

              {/* Removed explicit View Snapshot button; switching is automatic by month selection */}

              {/* Refresh Button */}
              <GlowButton 
                variant="contained" 
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                size="small"
                disabled={loading}
              >
                Refresh
              </GlowButton>
            </Box>
          </Box>
        </motion.div>

        {/* Data Type Indicator */}
        {month && (
          <motion.div variants={itemVariants}>
            <Box sx={{ mb: 2 }}>
              <Chip
                icon={<HistoryIcon fontSize="small" />}
                label={`Snapshot: ${month}`}
                color="secondary"
                size="small"
              />
            </Box>
          </motion.div>
        )}

        {/* Tabs Navigation */}
        {tabs.length > 0 && (
          <motion.div variants={itemVariants}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 3,
              pb: 0.5,
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AnimatedTabs 
                  tabs={tabs}
                  value={activeTab}
                  onChange={onTabChange}
                  variant="standard"
                  color="primary"
                  size="medium"
                  sx={{ minHeight: 48 }}
                />
                {month && (
                  <Chip
                    icon={<HistoryIcon fontSize="small" />}
                    label={`Snapshot: ${month}`}
                    color="secondary"
                    size="small"
                    sx={{ ml: 2 }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 500 
                  }}
                >
                  <strong>{brandCount}</strong> Brands | <strong>{campaignCount}</strong> Campaigns
                </Typography>
                <IconButton size="small" sx={{ ml: 1 }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </motion.div>
        )}

        {/* Content Area */}
        <motion.div variants={itemVariants}>
          {children}
        </motion.div>
      </motion.div>
    </Box>
  );
};

export default DashboardLayout;